import type { Rol } from '@pasos/schemas';

/**
 * Sesión del navegador.
 *
 * El token vive en memoria y en `sessionStorage`, no en `localStorage`: se va al
 * cerrar la pestaña, que es el comportamiento correcto para datos de salud en un
 * celular que puede quedar prestado o desatendido. La sesión del servidor además
 * corta por inactividad a los 30 minutos (S-09).
 *
 * El paciente y el cuidador no pierden nada con eso: al volver, la app renueva
 * la sesión con el **dispositivo recordado**, una cookie httpOnly que este código
 * no puede leer y que sólo viaja al endpoint de renovación (E3.24).
 *
 * El rol se guarda junto al token para una sola cosa: saber, cuando la sesión
 * se corta, por dónde se vuelve a entrar (contraseña o dispositivo). No decide
 * nada de lo que se muestra: eso sale siempre de `/yo`.
 */

const CLAVE = 'pasos.sesion';

export type Sesion = { idToken: string; rol?: Rol };

let enMemoria: Sesion | null = null;

/**
 * Quienes quieren enterarse de que la app **acaba de tener sesión** (P-132).
 *
 * Una sesión aparece por tres puertas: el canje de un enlace, la renovación con
 * el dispositivo recordado al abrir la app y la renovación silenciosa ante un
 * 401. Las tres terminan acá, en `guardarSesion`, así que avisar desde acá es
 * la única forma de no olvidarse de ninguna. La que faltaba era la segunda: es
 * como vuelve el paciente que cerró la app (INGRESO-1).
 */
const alGuardarse = new Set<() => void>();

export function suscribirseALaSesion(escucha: () => void): () => void {
  alGuardarse.add(escucha);
  return () => {
    alGuardarse.delete(escucha);
  };
}

export function guardarSesion(sesion: Sesion): void {
  enMemoria = sesion;
  try {
    sessionStorage.setItem(CLAVE, JSON.stringify(sesion));
  } catch {
    // Modo privado o almacenamiento bloqueado: la sesión vive en memoria y se
    // pierde al recargar. Es degradación aceptable, no un error que mostrar.
  }
  for (const escucha of alGuardarse) escucha();
}

export function leerSesion(): Sesion | null {
  if (enMemoria) return enMemoria;
  try {
    const crudo = sessionStorage.getItem(CLAVE);
    if (!crudo) return null;
    const sesion = JSON.parse(crudo) as Sesion;
    enMemoria = sesion;
    return sesion;
  } catch {
    return null;
  }
}

export function borrarSesion(): void {
  enMemoria = null;
  try {
    sessionStorage.removeItem(CLAVE);
  } catch {
    // Ídem: nada que hacer y nada que mostrar.
  }
}
